import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { formatDateTime } from 'v3/utils/formatter';
import Lightbox from 'components/Lightbox';
import Modal from 'v3/components/Modal';
import PDFView from 'v3/components/PDFView';
import StringBuilder from 'utils/StringBuilder';
import { getToken } from 'services/auth';

export function Attachment({
  attachment,
  attachments,
}) {

  const [isShowingModal, setShowingModal] = useState(false);
  const [preparedAttachmentsWithoutPDF, setPreparedAttachmentsWithoutPDF] = useState([]);
  const [preparedAttachmentPath, setPreparedAttachmentPath] = useState(null);

  function resolveAttachmentPath(path) {
    if (!path || typeof path !== 'string') return null;
    const input = path.trim();
    if (!input) return null;

    const baseCandidates = [
      import.meta.env.VITE_REPORT_API_URL,
      import.meta.env.VITE_COOPERPLACE_API_URL,
      typeof window !== 'undefined' ? window.location.origin : null,
    ].filter(Boolean);

    const hasProtocol = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(input);
    const isProtocolRelative = input.startsWith('//');

    if (hasProtocol) return input;
    if (isProtocolRelative) {
      const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
      return `${protocol}${input}`;
    }

    for (const base of baseCandidates) {
      try {
        return new URL(input, base).href;
      } catch (_) {
        // try next base
      }
    }

    try {
      return new URL(input, 'http://localhost').href;
    } catch (_) {
      return input;
    }
  }

  function getExtension(path) {
    if (!path || typeof path !== 'string') return '';
    try {
      const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
      const pathname = new URL(path, base).pathname || '';
      const parts = pathname.split('.');
      return (parts[parts.length - 1] || '').toLowerCase();
    } catch (_) {
      const withoutQuery = path.split('?')[0].split('#')[0];
      const parts = withoutQuery.split('.');
      return (parts[parts.length - 1] || '').toLowerCase();
    }
  }

  const normalizedAttachments = useMemo(
    () =>
      (attachments || []).map((item) => ({
        ...item,
        full_path: resolveAttachmentPath(
          item?.full_path || item?.media_full_path || item?.media_path || item?.path || item?.url,
        ),
      })),
    [attachments],
  );

  const attachmentsWithoutPDF = useMemo(() => {
    return normalizedAttachments.filter(item => {
      if (!item?.full_path) return item;
      if (getExtension(item.full_path) === 'pdf') return null;
      return item;
    });
  }, [normalizedAttachments]);

  const [lightbox, setLightbox] = useState({
    isOpen: false,
    attachments: [],
    index: 0,
  });
  const attachmentPath = useMemo(
    () =>
      resolveAttachmentPath(
        attachment?.full_path ||
          attachment?.media_full_path ||
          attachment?.media_path ||
          attachment?.path ||
          attachment?.url,
      ),
    [attachment],
  );

  async function fetchBlobUrl(path) {
    if (!path) return null;
    try {
      const token = getToken();
      const response = await axios.get(path, {
        responseType: 'blob',
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined,
      });
      return URL.createObjectURL(response.data);
    } catch (_) {
      return null;
    }
  }

  useEffect(() => {
    let isMounted = true;
    const createdUrls = [];

    async function prepareAttachments() {
      const prepared = await Promise.all(
        attachmentsWithoutPDF.map(async (item) => {
          const blobUrl = await fetchBlobUrl(item.full_path);
          if (blobUrl) createdUrls.push(blobUrl);
          return {
            ...item,
            full_path: blobUrl || item.full_path,
          };
        }),
      );

      if (isMounted) {
        setPreparedAttachmentsWithoutPDF(prepared);
      }
    }

    prepareAttachments();

    return () => {
      isMounted = false;
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [attachmentsWithoutPDF]);

  useEffect(() => {
    let isMounted = true;
    let createdUrl = null;

    async function prepareCurrentAttachmentPath() {
      if (!attachmentPath) {
        if (isMounted) setPreparedAttachmentPath(null);
        return;
      }

      const blobUrl = await fetchBlobUrl(attachmentPath);
      if (!isMounted) {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        return;
      }

      createdUrl = blobUrl;
      setPreparedAttachmentPath(blobUrl || attachmentPath);
    }

    prepareCurrentAttachmentPath();

    return () => {
      isMounted = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [attachmentPath]);

  const lightboxIndexes = useMemo(() => {
    const next = (lightbox.index + 1) % lightbox.attachments?.length;
    const previous =
      (lightbox.index + lightbox.attachments.length - 1) %
      lightbox.attachments.length;
    return { next, previous };
  }, [lightbox.index, lightbox.attachments]);

  function onClickPreview(id) {
    const previewAttachments =
      preparedAttachmentsWithoutPDF.length > 0 ? preparedAttachmentsWithoutPDF : attachmentsWithoutPDF;
    if (!previewAttachments.length) return;

    const foundIndex = id ? previewAttachments.findIndex(a => a.media_id === id) : 0;
    const index = foundIndex >= 0 ? foundIndex : 0;
    setLightbox(prev => ({
      ...prev,
      index,
      attachments: previewAttachments,
      isOpen: true,
    }));
  }

  useEffect(() => {
    const currentPath = attachmentPath;
    if (!currentPath) return;

    if (getExtension(currentPath) === 'pdf') {
      setShowingModal(true);
    } else {
      onClickPreview(attachment?.media_id);
    }
  }, [attachment, attachmentPath]);

  function getImagePath(index, alwaysDisplay) {
    const isSameAsIndex =
      lightbox.index === lightboxIndexes.next &&
      lightbox.index === lightboxIndexes.previous;

    if (!alwaysDisplay && isSameAsIndex && lightbox.attachments.length === 1) {
      return null;
    }
    return lightbox.attachments[index]?.full_path;
  }

  function getDocumentMetadata() {
    const document = lightbox.attachments[lightbox.index];
    const description = document?.description || 'Sem descrição';
    const type = document?.documentType;
    const due_date = formatDateTime(document?.due_date);
    return { description, due_date, type };
  }

  const lightboxTitle = useMemo(() => {
    const builder = new StringBuilder(' ');
    const documentMetadata = getDocumentMetadata();
    builder
      .append(`${lightbox.index + 1}/${lightbox.attachments?.length}`)
      .append('|')
      .append(documentMetadata.description)
      .append(documentMetadata.type)
      .append(`(${documentMetadata.due_date})`);

    return builder.build();
  }, [lightbox.index, lightbox.attachments]);

  return (
    <div>
      <Modal
        show={isShowingModal}
        dialogClassName="modal-90w"
        onHide={() => setShowingModal(false)}
        heading="Visualizar PDF"
        body={<PDFView file={preparedAttachmentPath || attachmentPath} />}
      />

      {lightbox.isOpen && (
        <Lightbox
          mainSrc={getImagePath(lightbox.index, true)}
          onCloseRequest={() =>
            setLightbox(prev => ({ ...prev, isOpen: false }))
          }
          onMovePrevRequest={() =>
            setLightbox(prev => ({
              ...prev,
              index: lightboxIndexes.previous,
            }))
          }
          onMoveNextRequest={() =>
            setLightbox(prev => ({
              ...prev,
              index: lightboxIndexes.next,
            }))
          }
          imageTitle={lightboxTitle}
        />
      )}

    </div>
  );
}
